/** Clean up OCR text for readability */
export function tidyText(input) {
  let text = input || '';
  text = text.replace(/\r\n?|\n/g, '\n');
  text = text.replace(/\n{3,}/g, '\n\n');
  text = text.replace(/^\s+|\s+$/gm, '');
  text = text.replace(/([A-Za-z])-?(\n)([A-Za-z])/g, '$1$3'); // de-hyphenate wraps
  text = text.replace(/([^.!?:])\n(?=[a-z])/g, '$1 ');       // join lines mid-sentence
  text = text.replace(/\s{2,}/g, ' ');
  return text.trim();
}

/** Simple grayscale + optional threshold (0–255) to clean noisy scans */
export function preprocessCanvas(src, threshold) {
  const w = src.width, h = src.height;
  const dst = document.createElement('canvas');
  dst.width = w; dst.height = h;
  const sctx = src.getContext('2d');
  const dctx = dst.getContext('2d');
  const img = sctx.getImageData(0, 0, w, h);
  const data = img.data;
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i], g = data[i+1], b = data[i+2];
    let y = 0.299*r + 0.587*g + 0.114*b; // luminance
    if (threshold != null) y = y > threshold ? 255 : 0;
    data[i] = data[i+1] = data[i+2] = y;
  }
  dctx.putImageData(img, 0, 0);
  return dst;
}

/** Recommend an OCR scale aiming for ~300 DPI (≈2600px long edge) */
export function recommendOcrScale(imgEl, targetLong = 2600) {
  const longSide = Math.max(imgEl.naturalWidth || imgEl.width, imgEl.naturalHeight || imgEl.height) || 1;
  const scale = targetLong / longSide;
  return Math.max(0.5, Math.min(5, +scale.toFixed(2)));
}

/**
 * Layout-aware, label-agnostic key–value extractor.
 * Pass the tesseract result.data object.
 */
export function extractKeyValues(ocrData, opts = {}) {
  const {
    synonyms = {},
    rowBandPx = 18,
    rightSearchPx = 1200,
    belowSearchPx = 600,
    preferOriginalKeys = false,   // << show original label text
    groupByCanon = true           // << merge by canonical key to avoid dupes
  } = opts;

  const words = (ocrData?.words || [])
    .filter(w => (w.conf ?? 0) > 40 && w.text && w.text.trim())
    .map(w => ({
      t: w.text.trim(),
      x0: w.bbox?.x0 ?? w.x0 ?? w.bbox?.x ?? 0,
      y0: w.bbox?.y0 ?? w.y0 ?? w.bbox?.y ?? 0,
      x1: w.bbox?.x1 ?? w.x1 ?? (w.bbox?.x ?? 0) + (w.bbox?.w ?? 0),
      y1: w.bbox?.y1 ?? w.y1 ?? (w.bbox?.y ?? 0) + (w.bbox?.h ?? 0),
      line: w.line ?? w.line_num ?? 0
    }));

  const linesMap = new Map();
  for (const w of words) {
    const key = w.line || `${Math.round(w.y0/rowBandPx)}`;
    if (!linesMap.has(key)) linesMap.set(key, []);
    linesMap.get(key).push(w);
  }
  const lines = [...linesMap.values()].map(ws => {
    const xs = ws.map(w=>w.x0), ys = ws.map(w=>w.y0), xe = ws.map(w=>w.x1), ye = ws.map(w=>w.y1);
    return {
      text: ws.map(w=>w.t).join(' ').replace(/\s{2,}/g,' ').trim(),
      x0: Math.min(...xs), y0: Math.min(...ys), x1: Math.max(...xe), y1: Math.max(...ye),
      words: ws
    };
  }).sort((a,b)=> a.y0!==b.y0 ? a.y0-b.y0 : a.x0-b.x0);

  const isLikelyLabel = (line) => {
    const t = line.text;
    if (!t) return false;
    if (/[A-Za-z]/.test(t) === false) return false;
    if (/:$/.test(t)) return true;
    if (/^[A-Z0-9 \-\/().]+$/.test(t) && t.length <= 38) return true;
    if (line.x0 < 250 && t.length <= 22) return true;
    return false;
  };

  const norm = s => s.toLowerCase().replace(/[^a-z0-9]+/g,' ').trim();
  const synIndex = Object.fromEntries(
    Object.entries(synonyms).flatMap(([canon, alts]) =>
      [canon, ...(alts||[])].map(a => [norm(a), canon])
    )
  );
  const normalizeKey = key => synIndex[norm(key)] || key;

  const sameRowBand = (l1, l2) => Math.abs(((l1.y0+l1.y1)/2) - ((l2.y0+l2.y1)/2)) <= rowBandPx;
  const isRightOf = (a,b) => a.x0 >= b.x1 && (a.x0 - b.x1) <= rightSearchPx;
  const isBelowOf = (a,b) => a.y0 >= b.y1 && (a.y0 - b.y1) <= belowSearchPx;

  const results = [];
  for (const label of lines.filter(isLikelyLabel)) {
    const right = lines.filter(l => sameRowBand(l, label) && isRightOf(l, label)).sort((a,b)=> a.x0-b.x0);
    const below = lines.filter(l => isBelowOf(l, label) && l.x0 >= label.x0 - 30 && l.x0 <= label.x0 + 300)
                       .sort((a,b)=> a.y0-b.y0 || a.x0-b.x0);
    const picked = right[0] || below[0];
    if (!picked) continue;

    const originalKey = label.text.replace(/[:：]+$/,'').trim();
    const canonKey = normalizeKey(originalKey);
    const displayKey = preferOriginalKeys ? originalKey : canonKey;

    const value = picked.text;

    // merge by canon (or by display key) to avoid duplicates across pages/variants
    const mergeKey = groupByCanon ? canonKey : displayKey;
    const existing = results.find(r => r.mergeKey === mergeKey);
    if (existing) {
      if (!existing.value.includes(value)) existing.value += ' | ' + value;
      existing.sources.push({label, picked});
    } else {
      results.push({ key: displayKey, canon: canonKey, value, mergeKey, sources: [{label, picked}] });
    }
  }

  // strip mergeKey before returning
  return { fields: results.map(({mergeKey, ...rest}) => rest), allLines: lines };
}

/** Convert Tesseract words → logical lines preserving order & positions */
export function wordsToLines(ocrData, rowBandPx = 18) {
  const words = (ocrData?.words || [])
    .filter(w => (w.conf ?? 0) > 40 && w.text && w.text.trim())
    .map(w => ({
      t: w.text, // keep raw
      x0: w.bbox?.x0 ?? w.x0 ?? w.bbox?.x ?? 0,
      y0: w.bbox?.y0 ?? w.y0 ?? w.bbox?.y ?? 0,
      x1: w.bbox?.x1 ?? w.x1 ?? (w.bbox?.x ?? 0) + (w.bbox?.w ?? 0),
      y1: w.bbox?.y1 ?? w.y1 ?? (w.bbox?.y ?? 0) + (w.bbox?.h ?? 0),
      line: w.line ?? w.line_num ?? 0,
    }));

  const linesMap = new Map();
  for (const w of words) {
    const key = w.line || `${Math.round(w.y0 / rowBandPx)}`;
    if (!linesMap.has(key)) linesMap.set(key, []);
    linesMap.get(key).push(w);
  }

  const lines = [...linesMap.values()].map(ws => {
    const xs = ws.map(w => w.x0), ys = ws.map(w => w.y0), xe = ws.map(w => w.x1), ye = ws.map(w => w.y1);
    const text = ws.map(w => w.t).join(' ');
    return {
      text,
      x0: Math.min(...xs), y0: Math.min(...ys), x1: Math.max(...xe), y1: Math.max(...ye),
      words: ws,
    };
  }).sort((a, b) => a.y0 !== b.y0 ? a.y0 - b.y0 : a.x0 - b.x0);

  // Page width/height approx from extrema
  const pageWidth = lines.length ? Math.max(...lines.map(l => l.x1)) : 0;
  const pageHeight = lines.length ? Math.max(...lines.map(l => l.y1)) : 0;

  return { lines, pageWidth, pageHeight };
}

/** Very light heuristic section/heading detection */
export function detectSections(lines, pageWidth) {
  const isLikelyHeading = (line) => {
    const t = (line.text || '').trim();
    if (!t) return false;
    const len = t.length;
    const capsRatio = len ? (t.replace(/[^A-Z]/g, '').length / len) : 0;
    const centered = line.x0 > pageWidth * 0.2 && line.x1 < pageWidth * 0.8;
    return (
      /:\s*$/.test(t) ||                              // ends with ':'
      (capsRatio > 0.6 && len <= 40) ||               // mostly caps
      (centered && len <= 48)                         // visually centered short line
    );
  };

  const sections = [];
  let current = { title: 'Body', blocks: [] };

  for (let i = 0; i < lines.length; i++) {
    const ln = lines[i];
    if (isLikelyHeading(ln)) {
      if (current.blocks.length) sections.push(current);
      current = { title: ln.text.replace(/[:：]+$/, '').trim(), blocks: [] };
    } else {
      current.blocks.push({ type: 'line', line: ln });
    }
  }
  if (current.blocks.length) sections.push(current);
  return sections;
}

/**
 * Detect simple tables by finding stable column gaps across consecutive lines.
 * Returns array of table blocks with rows/cells, and a mask of consumed lines.
 */
export function detectTables(lines, opts = {}) {
  const {
    minLines = 3,
    gapFactor = 3.2,       // gap > gapFactor * median char width → column break
    charWidthClamp = [3, 18],
  } = opts;

  const consumed = new Set();
  const tables = [];

  const estimateCharWidth = (ln) => {
    const len = Math.max(ln.text.length, 1);
    return Math.max(charWidthClamp[0], Math.min(charWidthClamp[1], (ln.x1 - ln.x0) / len));
  };

  // Scan blocks of adjacent, unconsumed lines
  for (let i = 0; i < lines.length; i++) {
    if (consumed.has(i)) continue;

    // Try to extend a candidate block
    const blockIdx = [i];
    for (let j = i + 1; j < lines.length; j++) {
      if (consumed.has(j)) break;
      const prev = lines[j - 1], cur = lines[j];
      const rowGap = cur.y0 - prev.y0;
      if (rowGap > (prev.y1 - prev.y0) * 2.2) break; // big vertical gap → block ended
      blockIdx.push(j);
    }
    if (blockIdx.length < minLines) continue;

    // Compute candidate column breaks from consistent large gaps in each line
    const columnCuts = [];
    const allSplits = [];
    for (const idx of blockIdx) {
      const ln = lines[idx];
      const cw = estimateCharWidth(ln);
      const gapThresh = cw * gapFactor;
      const splits = [];
      const ws = ln.words.slice().sort((a, b) => a.x0 - b.x0);
      for (let k = 1; k < ws.length; k++) {
        const prev = ws[k - 1];
        const cur = ws[k];
        const gap = cur.x0 - prev.x1;
        if (gap > gapThresh) splits.push((prev.x1 + cur.x0) / 2);
      }
      allSplits.push(splits);
    }

    // Keep splits that appear in most lines
    const flat = allSplits.flat();
    if (!flat.length) continue;
    flat.sort((a, b) => a - b);
    const merged = [];
    const radius = 15; // px merge radius
    for (const cut of flat) {
      if (!merged.length || Math.abs(merged[merged.length - 1] - cut) > radius) {
        merged.push(cut);
      }
    }
    if (merged.length === 0) continue;

    // Now build rows using these cuts
    const rows = [];
    for (const idx of blockIdx) {
      const ln = lines[idx];
      const cells = [];
      const segments = [ln.x0, ...merged, ln.x1];
      for (let c = 0; c < segments.length - 1; c++) {
        const left = segments[c], right = segments[c + 1];
        const cellText = ln.words
          .filter(w => w.x0 >= left - 1 && w.x1 <= right + 1)
          .sort((a, b) => a.x0 - b.x0)
          .map(w => w.t)
          .join(' ')
          .trim();
        cells.push(cellText);
      }
      // Skip empty lines
      if (cells.join('').trim().length === 0) continue;
      rows.push(cells);
    }

    if (rows.length >= minLines) {
      // mark consumed
      blockIdx.forEach(k => consumed.add(k));
      tables.push({ type: 'table', rows });
    }
  }

  return { tables, consumed };
}

/** Build a structured representation from OCR output */
export function toStructured(ocrData, opts = {}) {
  const { lines, pageWidth, pageHeight } = wordsToLines(ocrData, opts.rowBandPx || 18);
  const sections = detectSections(lines, pageWidth);

  // Detect tables within each section (consume line blocks so they don't duplicate as paragraphs)
  const blocks = [];
  const globalConsumed = new Set();
  const { tables, consumed } = detectTables(lines, opts.table || {});
  for (const idx of consumed) globalConsumed.add(idx);

  // Forms (KV) using your existing extractor
  const kv = extractKeyValues(ocrData, opts.kv || {});

  // Now emit section blocks
  for (const sec of sections) {
    const secBlocks = [];
    // Tables that fall within this section's vertical span
    const secY0 = Math.min(...sec.blocks.map(b => b.line.y0));
    const secY1 = Math.max(...sec.blocks.map(b => b.line.y1));

    // Paragraphs: lines not consumed by tables
    const paraLines = sec.blocks
      .filter(b => {
        const i = lines.indexOf(b.line);
        return i >= 0 && !globalConsumed.has(i);
      })
      .map(b => b.line);

    // Merge contiguous lines into paragraphs preserving newlines inside blocks
    let cur = [];
    let lastY = -Infinity;
    const flush = () => {
      if (!cur.length) return;
      const text = cur.map(l => l.text).join('\n'); // preserve line breaks
      secBlocks.push({ type: 'paragraph', text });
      cur = [];
    };
    for (const ln of paraLines) {
      if (ln.y0 - lastY > (ln.y1 - ln.y0) * 2) { // large vertical gap → new paragraph
        flush();
      }
      cur.push(ln);
      lastY = ln.y0;
    }
    flush();

    // Attach tables that intersect the section
    for (const t of tables) {
      // crude intersection by any row y in range — we don't carry y in table rows, so attach broadly
      secBlocks.push(t);
    }

    // Attach KVs seen within section y-range
    const kvFields = (kv.fields || []).filter(f => {
      // accept all; optional: refine via f.sources[0].label.y0 within [secY0, secY1]
      return true;
    });
    if (kvFields.length) secBlocks.push({ type: 'keyValues', fields: kvFields });

    blocks.push({ type: 'section', title: sec.title, blocks: secBlocks });
  }

  return { width: pageWidth, height: pageHeight, blocks };
}

/** Renderers **/
export function renderPlain(struct) {
  const out = [];
  for (const b of struct.blocks) {
    if (b.type === 'section') {
      out.push(`\n\n## ${b.title}\n`);
      for (const sb of b.blocks) {
        if (sb.type === 'paragraph') {
          out.push(sb.text + '\n');
        } else if (sb.type === 'table') {
          // simple TSV-like plain text
          for (const row of sb.rows) out.push(row.join('\t'));
        } else if (sb.type === 'keyValues') {
          for (const f of sb.fields) out.push(`${f.key}: ${f.value}`);
        }
      }
    }
  }
  return out.join('\n').trim();
}

export function renderMarkdown(struct) {
  const esc = (s) => s.replace(/\|/g, '\\|');
  const out = [];
  for (const b of struct.blocks) {
    if (b.type === 'section') {
      out.push(`\n\n### ${b.title}`);
      for (const sb of b.blocks) {
        if (sb.type === 'paragraph') {
          // Use fenced block to preserve intra-line spacing exactly as OCR gave it
          out.push('\n```text');
          out.push(sb.text);
          out.push('```');
        } else if (sb.type === 'table') {
          if (!sb.rows.length) continue;
          const colCount = Math.max(...sb.rows.map(r => r.length));
          const header = sb.rows[0];
          const hasHeader = header.every(c => /[A-Za-z]/.test(c));
          const headerRow = hasHeader ? header : Array.from({ length: colCount }, (_, i) => `Col ${i + 1}`);
          out.push('\n' + headerRow.map(esc).join(' | '));
          out.push(headerRow.map(() => '---').join(' | '));
          const bodyRows = hasHeader ? sb.rows.slice(1) : sb.rows;
          for (const row of bodyRows) {
            const cells = [...row];
            while (cells.length < colCount) cells.push('');
            out.push(cells.map(esc).join(' | '));
          }
        } else if (sb.type === 'keyValues') {
          for (const f of sb.fields) out.push(`- **${esc(f.key)}**: ${esc(f.value)}`);
        }
      }
    }
  }
  return out.join('\n').trim();
}

export function renderJSON(struct) {
  return JSON.stringify(struct, null, 2);
}